/**
 * @fileoverview Cost linking requirement popover for classification rules.
 * @module features/classification-rules/components/RuleCostLinkPopover
 */

import { useState, type ReactElement } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { cn } from '@/shared/lib/utils'
import { useTranslation } from 'react-i18next'
import type {
  ClassificationRuleResponse,
  CostLinkingRequirement,
} from '../schemas/classificationRules'

export interface RuleCostLinkPopoverProps {
  rule: ClassificationRuleResponse
  className?: string
  onApply: (patch: {
    costLinkingRequirement: CostLinkingRequirement | null
  }) => Promise<void> | void
}

export default function RuleCostLinkPopover({
  rule,
  className,
  onApply,
}: RuleCostLinkPopoverProps): ReactElement {
  const { t } = useTranslation('features-payments')
  const [open, setOpen] = useState(false)
  const [pending, setPending] = useState(false)

  const LABELS: Record<CostLinkingRequirement, string> = {
    REQUIRED: t('costLinkPopover.required', { defaultValue: 'Required' }),
    NOT_NEEDED: t('costLinkPopover.notNeeded', { defaultValue: 'Not needed' }),
    OPTIONAL: t('costLinkPopover.optional', { defaultValue: 'Optional' }),
  }

  const currentLabel = rule.costLinkingRequirement ? LABELS[rule.costLinkingRequirement] : '—'

  const apply = async (value: CostLinkingRequirement | null): Promise<void> => {
    setPending(true)
    try {
      await onApply({ costLinkingRequirement: value })
      setOpen(false)
    } finally {
      setPending(false)
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn('px-2 justify-start', className)}
        >
          {currentLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <div className="flex flex-col gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={pending}
            onClick={() => void apply('REQUIRED')}
          >
            {t('costLinkPopover.required', { defaultValue: 'Required' })}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={pending}
            onClick={() => void apply('OPTIONAL')}
          >
            {t('costLinkPopover.optional', { defaultValue: 'Optional' })}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={pending}
            onClick={() => void apply('NOT_NEEDED')}
          >
            {t('costLinkPopover.notNeeded', { defaultValue: 'Not needed' })}
          </Button>
          <div className="h-px bg-border/50 my-1" />
          <Button type="button" variant="ghost" disabled={pending} onClick={() => void apply(null)}>
            {t('costLinkPopover.clear', { defaultValue: 'Clear' })}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
